import React, { useState, useEffect } from 'react';
import api, { formatApiErrorDetail } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import NotificationModal from '../components/NotificationModal';

const Proveedores = () => {
  const { user: currentUser } = useAuth();
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [filterEmailVerified, setFilterEmailVerified] = useState('');
  
  // Estados para notificaciones
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: null
  });
  
  // Verificar si el usuario actual es admin (rol 1)
  const isAdmin = currentUser?.rol === 1;

  // Cargar proveedores al montar
  useEffect(() => {
    loadProveedores();
  }, []);

  const loadProveedores = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/usuarios/proveedores');
      // Asegurar que siempre sea un array
      const data = Array.isArray(response.data) ? response.data : [];
      setProveedores(data);
    } catch (err) {
      console.error('Error loading proveedores:', err);
      setError('Error al cargar los proveedores');
      setProveedores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (id) => {
    try {
      const response = await api.get(`/api/v1/usuarios/${id}`);
      setSelectedProveedor(response.data);
      setShowDetails(true);
    } catch (err) {
      console.error('Error loading proveedor:', err);
      setNotification({
        show: true,
        title: 'Error',
        message: 'Error al cargar los detalles del proveedor',
        type: 'error',
        onConfirm: null
      });
    }
  };

  const handleToggleEmailVerification = async (proveedor) => {
    if (!isAdmin) {
      setNotification({
        show: true,
        title: 'Acción no permitida',
        message: 'Solo los administradores pueden cambiar el estado de verificación de email',
        type: 'warning',
        onConfirm: null
      });
      return;
    }

    const newStatus = !proveedor.is_email_verified;
    const confirmMessage = newStatus 
      ? `¿Está seguro de marcar el email de ${proveedor.razon_social} como verificado?`
      : `¿Está seguro de marcar el email de ${proveedor.razon_social} como no verificado?`;

    setNotification({
      show: true,
      title: 'Confirmar acción',
      message: confirmMessage,
      type: 'confirm',
      onConfirm: async () => {
        try {
          setLoading(true);
          const response = await api.patch(`/api/v1/usuarios/${proveedor.id_usuario}/toggle-email-verification`);
          
          // Actualizar el proveedor en la lista
          if (Array.isArray(proveedores)) {
            setProveedores(proveedores.map(prov => 
              prov.id_usuario === proveedor.id_usuario 
                ? { ...prov, is_email_verified: response.data.is_email_verified }
                : prov
            ));
          }

          // Si el proveedor está en el modal, actualizarlo también
          if (selectedProveedor && selectedProveedor.id_usuario === proveedor.id_usuario) {
            setSelectedProveedor(response.data);
          }

          setNotification({
            show: true,
            title: 'Éxito',
            message: `Estado de verificación de email ${newStatus ? 'activado' : 'desactivado'} exitosamente`,
            type: 'success',
            onConfirm: null
          });
        } catch (err) {
          console.error('Error toggling email verification:', err);
          const errorMsg = formatApiErrorDetail(err, 'Error al cambiar el estado de verificación de email');
          setNotification({
            show: true,
            title: 'Error',
            message: errorMsg,
            type: 'error',
            onConfirm: null
          });
        } finally {
          setLoading(false);
        }
      },
      showCancel: true
    });
  };

  // Filtrar proveedores
  const filteredProveedores = Array.isArray(proveedores) ? proveedores.filter((prov) => {
    const matchesSearch = !searchTerm ||
      (prov.razon_social && prov.razon_social.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (prov.email && prov.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (prov.ruc && prov.ruc.includes(searchTerm)) ||
      (prov.celular && prov.celular.includes(searchTerm));

    const matchesEstado = filterEstado === '' ||
      (filterEstado === 'true' && prov.estado === true) ||
      (filterEstado === 'false' && prov.estado === false);

    const matchesEmailVerified = filterEmailVerified === '' ||
      (filterEmailVerified === 'true' && prov.is_email_verified === true) ||
      (filterEmailVerified === 'false' && prov.is_email_verified === false);

    return matchesSearch && matchesEstado && matchesEmailVerified;
  }) : [];

  if (loading && (!Array.isArray(proveedores) || proveedores.length === 0)) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3 text-muted">Cargando proveedores...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="h3 mb-1 fw-bold">Proveedores</h1>
          <p className="text-muted mb-0">Búsqueda y consulta de proveedores registrados</p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadProveedores}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>
          Actualizar
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Filtros */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h5 className="mb-3">Filtros de búsqueda</h5>
        <div className="row g-3">
          <div className="col-md-4">
            <label className="form-label">Búsqueda general</label>
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por razón social, email, RUC o celular..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label">Filtrar por Estado</label>
            <select
              className="form-select"
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value)}
            >
              <option value="">Todos los estados</option>
              <option value="true">Activo</option>
              <option value="false">Inactivo</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Email Verificado</label>
            <select
              className="form-select"
              value={filterEmailVerified}
              onChange={(e) => setFilterEmailVerified(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="true">Verificado</option>
              <option value="false">No verificado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de proveedores */}
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '10px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            Lista de Proveedores ({filteredProveedores.length})
          </h5>
        </div>

        {filteredProveedores.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            {!Array.isArray(proveedores) || proveedores.length === 0
              ? 'No hay proveedores registrados aún.'
              : 'No se encontraron proveedores con los filtros aplicados.'}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#667eea', color: 'white' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>ID</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Razón Social</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>RUC</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Email</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Celular</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Estado</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Email Verificado</th>
                  <th style={{ padding: '1rem', textAlign: 'left', border: '1px solid #ddd' }}>Fecha Registro</th>
                  <th style={{ padding: '1rem', textAlign: 'center', border: '1px solid #ddd' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(filteredProveedores) && filteredProveedores.map((prov) => (
                  <tr key={prov.id_usuario}>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{prov.id_usuario}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{prov.razon_social}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{prov.ruc}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{prov.email}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>{prov.celular || 'N/A'}</td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: prov.estado ? '#c6f6d5' : '#fed7d7',
                        color: '#2d3748',
                        fontWeight: '500',
                        fontSize: '0.875rem'
                      }}>
                        {prov.estado ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        background: prov.is_email_verified ? '#c6f6d5' : '#fed7d7',
                        color: '#2d3748',
                        fontWeight: '500',
                        fontSize: '0.875rem'
                      }}>
                        {prov.is_email_verified ? 'Verificado' : 'No verificado'}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd' }}>
                      {prov.fecha_registro
                        ? new Date(prov.fecha_registro).toLocaleDateString('es-PE')
                        : 'N/A'}
                    </td>
                    <td style={{ padding: '1rem', border: '1px solid #ddd', textAlign: 'center' }}>
                      <div className="d-flex gap-2 justify-content-center">
                        <button
                          onClick={() => handleView(prov.id_usuario)}
                          className="btn btn-info btn-sm"
                          style={{ fontSize: '0.875rem' }}
                          title="Ver detalles"
                        >
                          <i className="bi bi-eye me-1"></i>
                          Ver
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => handleToggleEmailVerification(prov)}
                            className={`btn btn-sm ${prov.is_email_verified ? 'btn-warning' : 'btn-success'}`}
                            style={{ fontSize: '0.875rem' }}
                            title={prov.is_email_verified ? 'Marcar como no verificado' : 'Marcar como verificado'}
                            disabled={loading}
                          >
                            <i className={`bi ${prov.is_email_verified ? 'bi-x-circle' : 'bi-check-circle'} me-1`}></i>
                            {prov.is_email_verified ? 'No Verificado' : 'Verificado'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Detalles */}
      {showDetails && selectedProveedor && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '10px',
            padding: '2rem',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            width: '100%'
          }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="h4 mb-1 fw-bold">Detalles del Proveedor</h2>
                <p className="text-muted mb-0">#{selectedProveedor.id_usuario}</p>
              </div>
              <button
                onClick={() => {
                  setShowDetails(false);
                  setSelectedProveedor(null);
                }}
                className="btn btn-outline-secondary"
              >
                <i className="bi bi-x-circle me-2"></i>
                Cerrar
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong>Tipo de Persona:</strong> {selectedProveedor.tipo_persona || 'N/A'}
              </div>
              <div>
                <strong>RUC:</strong> {selectedProveedor.ruc || 'N/A'}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <strong>Razón Social:</strong> {selectedProveedor.razon_social || 'N/A'}
              </div>
              <div>
                <strong>Email:</strong> {selectedProveedor.email || 'N/A'}
              </div>
              <div>
                <strong>Celular:</strong> {selectedProveedor.celular || 'N/A'}
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <strong>Dirección:</strong> {selectedProveedor.direccion || 'N/A'}
              </div>
              <div>
                <strong>Estado:</strong>{' '}
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  background: selectedProveedor.estado ? '#c6f6d5' : '#fed7d7',
                  color: '#2d3748',
                  fontWeight: '500'
                }}>
                  {selectedProveedor.estado ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div>
                <strong>Email Verificado:</strong>{' '}
                <span style={{
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  background: selectedProveedor.is_email_verified ? '#c6f6d5' : '#fed7d7',
                  color: '#2d3748',
                  fontWeight: '500'
                }}>
                  {selectedProveedor.is_email_verified ? 'Sí' : 'No'}
                </span>
                {isAdmin && (
                  <button
                    onClick={() => handleToggleEmailVerification(selectedProveedor)}
                    className={`btn btn-sm ms-2 ${selectedProveedor.is_email_verified ? 'btn-warning' : 'btn-success'}`}
                    disabled={loading}
                    title={selectedProveedor.is_email_verified ? 'Marcar como no verificado' : 'Marcar como verificado'}
                  >
                    <i className={`bi ${selectedProveedor.is_email_verified ? 'bi-x-circle' : 'bi-check-circle'} me-1`}></i>
                    {selectedProveedor.is_email_verified ? 'Marcar No Verificado' : 'Marcar Verificado'}
                  </button>
                )}
              </div>
              <div>
                <strong>Fecha de Registro:</strong>{' '}
                {selectedProveedor.fecha_registro
                  ? new Date(selectedProveedor.fecha_registro).toLocaleString('es-PE')
                  : 'N/A'}
              </div>
              {selectedProveedor.fecha_edicion && (
                <div>
                  <strong>Fecha de Edición:</strong>{' '}
                  {new Date(selectedProveedor.fecha_edicion).toLocaleString('es-PE')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Notificaciones */}
      <NotificationModal
        show={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        title={notification.title}
        message={notification.message}
        type={notification.type}
        onConfirm={notification.onConfirm}
        showCancel={notification.type === 'confirm'}
      />
    </div>
  );
};

export default Proveedores;

